import React, { useState } from "react";
import { X, Send, AlertCircle, CheckCircle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../config/featureFlags";
import { termsContent } from "../legal/terms";
import { privacyContent } from "../legal/privacy";
import { acceptableUseContent } from "../legal/acceptableUse";
import { refundsContent } from "../legal/refunds";
import { aiRankingContent } from "../legal/aiRanking";
import { safetyContent } from "../legal/safety";

export type LegalTab =
  | "terms"
  | "privacy"
  | "acceptable-use"
  | "contact-removal"
  | "ai-ranking"
  | "safety"
  | "refunds";

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: LegalTab;
  apiFetch: (url: string, options?: RequestInit) => Promise<Response>; // i18n-ignore
}

export function LegalModal({ isOpen, onClose, initialTab = "terms", apiFetch }: LegalModalProps) {
  const [activeTab, setActiveTab] = useState<LegalTab>(initialTab);
  const [submitStatus, setSubmitStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);

  if (!isOpen) return null;

  const renderDisclaimer = () => (
    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-xs leading-relaxed flex items-start gap-2">
      <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
      <span>
        <strong>Legal Disclaimer:</strong> This page is a product safety template and should be reviewed by a qualified attorney before public release. // i18n-ignore
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs cursor-pointer" onClick={onClose}>
      <div className="relative w-full max-w-4xl h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden text-slate-800 cursor-default" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <span>BizNearYou Compliance &amp; Legal Center</span>
              <span className="text-[10px] font-normal uppercase bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                Beta v1.0
              </span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Find local work. Reach local employers. Safely.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded-lg transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Workspace Grid */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Left Sidebar Menu */}
          <div className="w-64 border-r border-slate-100 bg-slate-50/50 p-4 flex flex-col gap-1 overflow-y-auto shrink-0">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-2 block">
              Policies &amp; Agreements
            </span>
            <button
              onClick={() => { setActiveTab("terms"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "terms" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              Terms of Service
            </button>
            <button
              onClick={() => { setActiveTab("privacy"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "privacy" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => { setActiveTab("acceptable-use"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "acceptable-use" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              Acceptable Use Policy
            </button>
            <button
              onClick={() => { setActiveTab("refunds"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "refunds" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              Billing &amp; Refunds
            </button>

            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 mb-2 px-2 block">
              Safety &amp; Disclosures
            </span>
            <button
              onClick={() => { setActiveTab("ai-ranking"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "ai-ranking" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              {AI_FEATURES_ENABLED ? "AI & Match Ranking Explainer" : "Match & Discovery Ranking Explainer"}
            </button>
            <button
              onClick={() => { setActiveTab("safety"); setSubmitStatus(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === "safety" ? "bg-indigo-50 text-indigo-700 font-bold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              Safety &amp; Crisis Resources
            </button>
          </div>

          {/* Right Main Content */}
          <div className="flex-1 p-6 overflow-y-auto flex flex-col">
            {renderDisclaimer()}

            {/* TERMS OF SERVICE */}
            {activeTab === "terms" && (
              <div className="prose prose-sm text-slate-700 flex-1">
                <h3 className="text-base font-bold text-slate-900 mb-2">{termsContent.title}</h3>
                <p className="text-xs text-slate-500 mb-4">Last Updated: {termsContent.lastUpdated}</p>
                <div className="space-y-3 text-xs leading-relaxed">
                  <p>
                    Welcome to <strong>BizNearYou</strong> (&quot;the Platform&quot;). BizNearYou provides a map-based solution for users to discover local business listings, job opportunities, and engage in employer outreach and professional communication. // i18n-ignore
                  </p>
                  {termsContent.sections.map((sec, idx) => (
                    <p key={idx}>
                      <strong>{sec.heading}:</strong> {sec.text}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* PRIVACY POLICY */}
            {activeTab === "privacy" && (
              <div className="prose prose-sm text-slate-700 flex-1">
                <h3 className="text-base font-bold text-slate-900 mb-2">{privacyContent.title}</h3>
                <p className="text-xs text-slate-500 mb-4">Last Updated: {privacyContent.lastUpdated}</p>
                <div className="space-y-3 text-xs leading-relaxed">
                  <p>{privacyContent.intro}</p>
                  {privacyContent.sections.map((sec, idx) => (
                    <div key={idx} className="space-y-2 mt-3">
                      <p><strong>{sec.heading}:</strong></p>
                      {sec.bullets ? (
                        <ul className="list-disc pl-5 space-y-1">
                          {sec.bullets.map((b, bIdx) => (
                            <li key={bIdx}>{b}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>{sec.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ACCEPTABLE USE POLICY */}
            {activeTab === "acceptable-use" && (
              <div className="prose prose-sm text-slate-700 flex-1">
                <h3 className="text-base font-bold text-slate-900 mb-2">{acceptableUseContent.title}</h3>
                <p className="text-xs text-slate-500 mb-4">Last Updated: {acceptableUseContent.lastUpdated}</p>
                <div className="space-y-3 text-xs leading-relaxed">
                  <p>{acceptableUseContent.intro}</p>
                  <p className="font-semibold text-rose-900">{acceptableUseContent.prohibitedHeading}</p>
                  <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                    {acceptableUseContent.prohibitedItems.map((item, idx) => (
                      <li key={idx}>
                        <strong>{item.title}:</strong> {item.description}
                      </li>
                    ))}
                  </ul>
                  <p>{acceptableUseContent.conclusion}</p>
                </div>
              </div>
            )}

            {/* BILLING & REFUNDS */}
            {activeTab === "refunds" && (
              <div className="prose prose-sm text-slate-700 flex-1">
                <h3 className="text-base font-bold text-slate-900 mb-2">{refundsContent.title}</h3>
                <p className="text-xs text-slate-500 mb-4">Last Updated: {refundsContent.lastUpdated}</p>
                <div className="space-y-3 text-xs leading-relaxed">
                  <p>{refundsContent.intro}</p>
                  {refundsContent.sections.map((sec, idx) => (
                    <p key={idx}>
                      <strong>{sec.heading}:</strong> {sec.text}
                    </p>
                  ))}
                </div>
              </div>
            )}


            {/* CONTACT REMOVAL (OPT-OUT) FORM */}
            {activeTab === "contact-removal" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 max-w-xl mx-auto space-y-4">
                <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center border border-rose-100">
                  <X className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">Unified Removal Requests</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  In order to streamline compliance and opt-out processing, all business listing and contact information removal requests have been consolidated into our primary support channel.
                </p>
                <p className="text-xs text-slate-500 leading-relaxed font-semibold">
                  Please submit your request using our unified Contact Page by selecting the "Request Business Profile Removal" topic option.
                </p>
              </div>
            )}

            {/* AI & RANKING EXPLANATION */}
            {activeTab === "ai-ranking" && (
              <div className="prose prose-sm text-slate-700 flex-1">
                <h3 className="text-base font-bold text-slate-900 mb-2">{aiRankingContent.title}</h3>
                <p className="text-xs text-slate-500 mb-4">Last Updated: {aiRankingContent.lastUpdated}</p>
                <div className="space-y-3 text-xs leading-relaxed">
                  <p>{aiRankingContent.intro}</p>
                  {aiRankingContent.sections.map((sec, idx) => (
                    <p key={idx}>
                      <strong>{sec.heading}:</strong> {sec.text}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* SAFETY & CRISIS RESOURCES */}
            {activeTab === "safety" && (
              <div className="prose prose-sm text-slate-700 flex-1 space-y-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{safetyContent.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {safetyContent.intro}
                  </p>
                </div>

                <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-950 text-xs">
                  <h4 className="font-bold text-rose-900 mb-1 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 text-rose-700" />
                    {safetyContent.emergency.heading}
                  </h4>
                  <p className="leading-relaxed">
                    {safetyContent.emergency.text}
                  </p>
                  <div className="mt-3 flex gap-3 font-semibold">
                    {safetyContent.emergency.calls.map((c, idx) => (
                      <a key={idx} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel={c.href.startsWith("http") ? "noreferrer" : undefined} className="underline text-rose-800 hover:text-rose-950">
                        {c.label}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
                  <p>
                    <strong>{safetyContent.global.text.split(":")[0]}:</strong>{safetyContent.global.text.split(":")[1]}
                  </p>
                  <p>
                    <strong>{safetyContent.guidance.text.split(":")[0]}:</strong>{safetyContent.guidance.text.split(":")[1]}
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-[11px] text-slate-400">
          <span>BizNearYou Safety Framework • Review required before launch</span>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-slate-250 text-slate-700 hover:bg-slate-300 rounded-lg font-bold transition cursor-pointer"
          >
            Close Center
          </button>
        </div>

      </div>
    </div>
  );
}
